import {
  type EssaiBotResult,
  type EventSubStatus,
  type OppositionDiscordLeveeIssue,
  type OppositionLeveeResult,
  type OppositionResult,
  RETURN_CODES,
  type ResumeTache,
  type ReturnCode,
  type RuntimeReason,
  type TwitchAccount,
} from "./contract";

/**
 * Les textes de la regie et ses petits formats, ecrits pour Nicolas (le seul admin) : courts, a
 * l'imperatif ou au constat, sans jargon. La page sera redessinee plus tard avec des maquettes ;
 * ces phrases, elles, disent ce qui se passe et ce qu'il faut faire.
 */

type Role = TwitchAccount["role"];

/**
 * Les deux comptes, dans l'ordre de la page : la chaine d'abord, puis le bot. `login` est le compte
 * Twitch attendu pour le role, une copie de `EXPECTED_LOGIN` (`backend/src/app/twitch/config.ts`) :
 * le frontend ne peut pas importer le backend, et `regie-texts.test.ts` garde les deux alignees.
 */
export const ROLES: readonly {
  readonly role: Role;
  readonly label: string;
  readonly login: string;
}[] = [
  { role: "chaine", label: "La chaîne", login: "kavalierogamedev" },
  { role: "bot", label: "IntendantBot, le bot", login: "intendantbot" },
];

type ReturnMessage = {
  readonly text: string;
  /** `ok` : la phrase rassure. `problem` : elle signale quelque chose a refaire. */
  readonly tone: "ok" | "problem";
};

/**
 * Une phrase par code de retour. `Record<ReturnCode, ...>` : un code ajoute au contrat sans sa
 * phrase ne compile pas.
 */
export const RETURN_MESSAGES: Record<ReturnCode, ReturnMessage> = {
  ok: { tone: "ok", text: "Le compte est autorisé." },
  refus: {
    tone: "problem",
    text: "L'autorisation a été refusée ou annulée sur Twitch. Rien n'a changé.",
  },
  etat_invalide: {
    tone: "problem",
    text: "La demande a expiré ou a déjà servi. Recommence depuis cette page.",
  },
  // `code_refuse` et `indisponible` couvrent aussi un identifiant ou un secret mal poses sur le
  // serveur (le backend les range ainsi) : la page ne peut pas le diagnostiquer, elle dit donc ou
  // regarder quand l'echec se repete.
  code_refuse: {
    tone: "problem",
    text: "Twitch a refusé de finir l'autorisation. Recommence ; si cela se répète, vérifie TWITCH_CLIENT_ID et TWITCH_CLIENT_SECRET sur le serveur.",
  },
  // Pas de fenetre privee : le retour de Twitch exige le cookie de la regie, donc le meme
  // navigateur. Dans une fenetre privee, il finirait sur un 401 en JSON brut.
  mauvais_compte: {
    tone: "problem",
    text: "Ce n'est pas le bon compte Twitch pour ce rôle. Dans ce même navigateur, déconnecte-toi de twitch.tv, connecte-toi avec le compte attendu, puis recommence.",
  },
  droits_manquants: {
    tone: "problem",
    text: "Twitch n'a pas accordé tous les droits demandés. Recommence en acceptant tout.",
  },
  indisponible: {
    tone: "problem",
    text: "Twitch ou la base de données n'a pas répondu, ou a répondu par une erreur. Réessaie ; si cela se répète, vérifie TWITCH_CLIENT_ID et TWITCH_CLIENT_SECRET sur le serveur.",
  },
};

/**
 * Vrai pour l'un des codes du contrat, et pour rien d'autre. Un test d'appartenance a la liste
 * fermee, pas `value in RETURN_MESSAGES` : `constructor` ou `__proto__` y passeraient.
 */
export function isReturnCode(value: unknown): value is ReturnCode {
  return typeof value === "string" && (RETURN_CODES as readonly string[]).includes(value);
}

/**
 * La phrase d'un code de retour, ou `null`. Le code vient de l'adresse (`?twitch=`), donc de
 * n'importe qui : tout ce qui n'est pas dans la liste fermee n'affiche rien, et le texte brut de
 * l'adresse n'est jamais rendu.
 */
export function returnMessage(value: unknown): ReturnMessage | null {
  return isReturnCode(value) ? RETURN_MESSAGES[value] : null;
}

/**
 * Les codes que Better Auth met dans `?error=` quand un lien magique n'ouvre pas la session (la
 * route `GET /api/auth/magic-link/verify` ramene sur `callbackURL`, c'est-a-dire la regie). Une
 * liste fermee, relevee dans le plugin `magicLink` de Better Auth et dans le crochet du template
 * (`backend/src/lab/auth/index.ts`) :
 * - `INVALID_TOKEN` : lien inconnu, perime (quinze minutes) ou deja servi ;
 * - `failed_to_create_user`, `failed_to_create_session`, `user_not_found` : le lien a ete consomme
 *   mais le compte ou la session n'ont pas pu etre prepares ;
 * - `signup_closed` : le compte n'existe pas encore et les inscriptions sont fermees (drapeau
 *   `signupOpen` d'Hermes).
 * Better Auth ajoute `error_description`, un texte libre que la page n'affiche jamais.
 */
export const LINK_ERROR_CODES = [
  "INVALID_TOKEN",
  "failed_to_create_user",
  "failed_to_create_session",
  "user_not_found",
  "signup_closed",
] as const;
export type LinkErrorCode = (typeof LINK_ERROR_CODES)[number];

const LINK_LOST = "Ce lien ne marche plus : il a expiré ou il a déjà servi. Demande-en un nouveau.";

/**
 * Une phrase par code. Le lien est perdu dans tous les cas sauf un : quand les inscriptions sont
 * fermees, un nouveau lien n'ouvrirait pas de compte avant qu'elles rouvrent, et le dire evite de
 * faire demander des liens pour rien.
 */
export const LINK_ERROR_MESSAGES: Record<LinkErrorCode, string> = {
  INVALID_TOKEN: LINK_LOST,
  failed_to_create_user: LINK_LOST,
  failed_to_create_session: LINK_LOST,
  user_not_found: LINK_LOST,
  signup_closed:
    "Les inscriptions sont fermées pour le moment : ce lien n'a pas pu créer ton compte. Demande-en un nouveau quand elles seront rouvertes.",
};

/** Vrai pour l'un des codes ci-dessus et pour rien d'autre : la liste fermee, pas un index direct. */
export function isLinkErrorCode(value: unknown): value is LinkErrorCode {
  return typeof value === "string" && (LINK_ERROR_CODES as readonly string[]).includes(value);
}

/**
 * La phrase d'un code d'erreur de lien magique, ou `null`. Le code vient de l'adresse
 * (`?error=`), donc de n'importe qui : un code inconnu n'affiche rien, et le texte brut de l'adresse
 * n'est jamais rendu.
 */
export function linkErrorMessage(value: unknown): string | null {
  return isLinkErrorCode(value) ? LINK_ERROR_MESSAGES[value] : null;
}

/**
 * Ce que Twitch retient du navigateur : le compte autorise est celui auquel ce navigateur est connecte
 * sur twitch.tv, et le retour de Twitch exige le cookie de la regie, donc le meme navigateur.
 */
export const BROWSER_NOTE =
  "Le compte Twitch utilisé est celui auquel ce navigateur est connecté sur twitch.tv. Pour le bot, change de compte sur twitch.tv dans ce même navigateur, pas dans une fenêtre privée.";

const DAY = new Intl.DateTimeFormat("fr-FR", { dateStyle: "long", timeZone: "Europe/Paris" });

/**
 * Le jour d'une date ISO 8601 (`2026-10-12T14:30:00.000Z`), en francais et a l'heure de Paris, ou
 * `null` si le texte n'est pas une date : `Intl` leve sur une date invalide, et une date illisible
 * ne doit pas faire tomber la page.
 */
export function formatDay(iso: string): string | null {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : DAY.format(date);
}

/**
 * Une phrase par raison du travail de fond. `Record<RuntimeReason, ...>` : une raison ajoutee au
 * contrat sans sa phrase ne compile pas. `ok` seul est une bonne nouvelle ; les autres disent ce qui
 * se passe, et ce qu'il reste a faire quand Nicolas peut agir.
 */
export const RUNTIME_MESSAGES: Record<RuntimeReason, { text: string; tone: "ok" | "problem" }> = {
  ok: { tone: "ok", text: "La connexion à Twitch tourne : les événements de la chaîne arrivent." },
  non_configure: {
    tone: "problem",
    text: "Twitch n'est pas configuré sur le serveur : rien n'est tenté tant que les variables manquent.",
  },
  verrou: {
    tone: "problem",
    text: "Une autre instance de l'app tient déjà la connexion à Twitch (ou la base n'a pas répondu). Celle-ci réessaie toutes les 30 secondes.",
  },
  compte_absent: {
    tone: "problem",
    text: "Le compte de la chaîne n'est pas autorisé, ou doit l'être de nouveau. Autorise-le ci-dessous ; la connexion démarrera toute seule.",
  },
  en_pause: {
    tone: "problem",
    text: "L'app est en pause ou en maintenance : la connexion à Twitch est fermée et reprendra à la reprise.",
  },
  arrete: {
    tone: "problem",
    text: "Le travail de fond n'a pas démarré ou l'app s'arrête : aucun événement n'est reçu.",
  },
};

/** L'etat de la connexion EventSub, en une phrase. Les dates illisibles disparaissent de la phrase. */
export function eventSubText(state: EventSubStatus): string {
  switch (state.kind) {
    case "arrete":
      return "EventSub : arrêté.";
    case "connexion":
      return "EventSub : connexion en cours.";
    case "connecte": {
      const since = formatTime(state.depuis);
      return since ? `EventSub : connecté depuis ${since}.` : "EventSub : connecté.";
    }
    case "coupe": {
      const since = formatTime(state.depuis);
      const attempts = `${state.tentatives} tentative${state.tentatives > 1 ? "s" : ""} depuis`;
      return since
        ? `EventSub : coupé depuis ${since} (${attempts} la coupure).`
        : `EventSub : coupé (${attempts} la coupure).`;
    }
  }
}

/**
 * Un nom lisible par type d'evenement du journal. Le type vient de la base : tout ce qui n'est pas
 * dans la liste est ecrit « Autre evenement », jamais le texte brut.
 */
const EVENT_LABELS: Readonly<Record<string, string>> = {
  "stream.online": "Début du direct",
  "stream.offline": "Fin du direct",
  "channel.update": "Changement de titre ou de jeu",
  "channel.follow": "Nouveau follow",
  "channel.subscribe": "Abonnement",
  "channel.subscription.gift": "Abonnements offerts",
  "channel.subscription.gift.anonymous": "Abonnements offerts (anonyme)",
  "channel.subscription.message": "Réabonnement",
  "channel.cheer": "Bits",
  "channel.cheer.anonymous": "Bits (anonyme)",
  "channel.raid": "Raid",
  "channel.channel_points_custom_reward_redemption.add": "Récompense de points",
  "channel.chat.message": "Message du chat",
  "channel.chat.notification": "Annonce du chat",
  "channel.ban": "Bannissement",
  "channel.poll.begin": "Sondage",
  "channel.hype_train.begin": "Début d'un hype train",
  "channel.hype_train.progress": "Progression du hype train",
  "channel.hype_train.end": "Fin du hype train",
};

export function eventLabel(type: string): string {
  return Object.hasOwn(EVENT_LABELS, type) ? (EVENT_LABELS[type] as string) : "Autre événement";
}

const TIME = new Intl.DateTimeFormat("fr-FR", {
  dateStyle: "long",
  timeStyle: "short",
  timeZone: "Europe/Paris",
});

/** Le jour et l'heure (Paris) d'une date ISO 8601, ou `null` si le texte n'est pas une date. */
export function formatTime(iso: string): string | null {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : TIME.format(date);
}

/** Une phrase par resultat de l'essai du bot : le texte de Twitch n'est jamais affiche. */
export const ESSAI_BOT_MESSAGES: Record<EssaiBotResult, { text: string; tone: "ok" | "problem" }> =
  {
    envoye: { tone: "ok", text: "Le bot a parlé dans le chat : « Le château est relié. »" },
    refuse: { tone: "problem", text: "Twitch a refusé le message : le bot n'a rien dit." },
    indisponible: {
      tone: "problem",
      text: "L'essai n'a pas pu avoir lieu : connexion inactive, compte du bot absent ou à réautoriser, ou un essai date de moins de dix secondes.",
    },
  };

/** Quand la requete elle-meme echoue (reseau, 4xx, 5xx, reponse hors contrat). */
export const ESSAI_BOT_FAILED = "L'essai n'a pas pu être envoyé. Réessaie dans un instant.";

/**
 * La phrase des abonnements que Twitch a refuses (les noms viennent de la liste de l'app, mis en mots
 * par `eventLabel`), ou `null` quand il n'y en a aucun. Les autres abonnements fonctionnent.
 */
export function refusedSubscriptionsText(types: readonly string[]): string | null {
  if (types.length === 0) {
    return null;
  }
  const names = [...new Set(types.map(eventLabel))].join(", ");
  const count = types.length;
  return `Twitch a refusé ${count} abonnement${count > 1 ? "s" : ""} (${names}). Les autres fonctionnent, la connexion reste ouverte.`;
}

/**
 * Une phrase par resultat de l'opposition d'un viewer. `Record<OppositionResult, ...>` : un resultat
 * ajoute au contrat sans sa phrase ne compile pas. Aucune ne cite la personne : la page ne rend jamais
 * un login ni un identifiant venu du serveur.
 */
export const OPPOSITION_MESSAGES: Record<
  OppositionResult,
  { text: string; tone: "ok" | "problem" }
> = {
  efface: {
    tone: "ok",
    text: "Les données de cette personne sont effacées, et La Taverne ne note plus rien d'elle.",
  },
  inscrit: {
    tone: "ok",
    text: "Cette personne n'était pas dans la base. Elle est inscrite : La Taverne ne notera rien d'elle.",
  },
  identifiant_necessaire: {
    tone: "problem",
    text: "Personne n'a ce login dans la base, rien n'a été fait. Donne son identifiant Twitch.",
  },
  plusieurs: {
    tone: "problem",
    text: "Plusieurs personnes ont porté ce login, rien n'a été fait. Donne l'identifiant Twitch de la bonne.",
  },
};

/** Quand la requete elle-meme echoue (reseau, 4xx, 5xx, reponse hors contrat). */
export const OPPOSITION_FAILED = "L'effacement n'a pas pu être fait. Réessaie dans un instant.";

/**
 * Une phrase par resultat de la levee d'une opposition (`opposition-form.tsx`, `LeverOppositionForm`). Meme regle :
 * `Record<OppositionLeveeResult, ...>`, et aucune ne cite la personne.
 */
export const OPPOSITION_LEVEE_MESSAGES: Record<
  OppositionLeveeResult,
  { text: string; tone: "ok" | "problem" }
> = {
  levee: {
    tone: "ok",
    text: "L'opposition est levée : La Taverne peut de nouveau noter cette personne, et elle peut rejoindre. Ce qui a été effacé ne revient pas. Sa carte peut renaître au prochain stream où elle compte, sauf si elle l'avait retirée : ce retrait reste. Si ses comptes étaient reliés, lève aussi son opposition Discord, avec son identifiant Discord.",
  },
  absente: {
    tone: "ok",
    text: "Cette personne n'avait pas d'opposition : rien n'a été changé.",
  },
};

/** Quand la requete de levee elle-meme echoue (reseau, 4xx, 5xx, reponse hors contrat). */
export const OPPOSITION_LEVEE_FAILED =
  "L'opposition n'a pas pu être levée. Réessaie dans un instant.";

/**
 * Une phrase par resultat de la levee d'une opposition Discord (`opposition-form.tsx`, `LeverOppositionDiscordForm`). Meme
 * regle : `Record<OppositionDiscordLeveeIssue, ...>`, et aucune ne cite le membre. Un echec de la requete dit la meme phrase
 * que celui de la levee Twitch (`OPPOSITION_LEVEE_FAILED`).
 */
export const OPPOSITION_DISCORD_LEVEE_MESSAGES: Record<
  OppositionDiscordLeveeIssue,
  { text: string; tone: "ok" | "problem" }
> = {
  levee: {
    tone: "ok",
    text: "L'opposition est levée : L'Écuyer peut de nouveau noter ce membre, et il peut relier son compte Twitch avec /lier. Ce qui a été effacé ne revient pas : il repart de zéro.",
  },
  absente: {
    tone: "ok",
    text: "Ce membre n'avait pas d'opposition : rien n'a été changé.",
  },
};

// --- La carte « Agent du PC » -----------------------------------------------------------------------
// L'agent est le programme du PC de Nicolas (`packages/atelier`) : il fait l'inventaire du stock de clips
// et les envoie a Castellan. La carte dit s'il est la, ce qu'il a ecrit en dernier, et ce qui a echoue.

export const AGENT_TITLE = "Agent du PC";
export const AGENT_LOADING = "Lecture de l'état de l'agent...";
/** Quand la requete elle-meme echoue (reseau, 4xx, 5xx, reponse hors contrat). */
export const AGENT_READ_FAILED = "L'état de l'agent n'a pas pu être lu. Réessaie dans un instant.";
export const AGENT_FAILURES_TITLE = "Tâches en échec";
export const AGENT_NO_FAILURES = "Aucune tâche en échec.";
/** Le texte visible du bouton qui relance une tache en echec. */
export const AGENT_RELAUNCH = "Relancer";
export const AGENT_RELAUNCHING = "Relance en cours...";
export const AGENT_RELAUNCHED = "Tâche relancée : l'agent la reprendra à son prochain passage.";
export const AGENT_RELAUNCH_FAILED = "La tâche n'a pas pu être relancée. Réessaie dans un instant.";
/** Sans reponse claire (delai, coupure) : la relance a pu aboutir, l'etat de l'agent est relu et dit ce qu'il en est. */
export const AGENT_RELAUNCH_INCERTAINE =
  "La relance n'a pas donné de réponse claire. Elle a peut-être abouti : l'état de l'agent est relu, vérifie la tâche avant de réessayer.";

/**
 * Au-dela de ce delai sans signe de vie, l'agent est dit eteint. Il s'annonce toutes les minutes tant que
 * la session Windows du PC est ouverte : dix minutes laissent passer une tache longue (un televersement)
 * sans fausse alerte.
 */
export const AGENT_ACTIF_MAX_MS = 10 * 60_000;

/**
 * Le nom accessible du bouton « Relancer » d'une tache : son texte visible, puis la tache par sa cle
 * (« Relancer la tâche televersement:clip:42 »). Chaque bouton de la carte a le sien, donc un lecteur
 * d'ecran n'annonce pas plusieurs fois « Relancer » tout court, et on trouve chacun par son nom. Le texte
 * visible est au debut du nom (WCAG 2.5.3 : l'etiquette est dans le nom). La cle est du texte de la file,
 * jamais interpretee.
 */
export function relaunchLabel(key: string): string {
  return `${AGENT_RELAUNCH} la tâche ${key}`;
}

/** Un nom lisible par type de tache de la file. `Record` : un type ajoute au contrat sans son nom ne compile pas. */
export const TASK_TYPE_LABELS: Record<ResumeTache["type"], string> = {
  inventaire: "Inventaire du stock",
  televersement: "Téléversement d'un clip",
  programmation: "Programmation",
  recolte: "Récolte",
  purge: "Purge",
};

export type AgentStatus = {
  readonly text: string;
  /**
   * `ok` : l'agent est la. `neutre` : il ne s'est jamais manifeste, ce n'est pas encore un incident.
   * `problem` : il est eteint, ou son signe de vie est illisible.
   */
  readonly tone: "ok" | "neutre" | "problem";
};

/** Une duree en minutes, en heures ou en jours pleins : « 25 min », « 3 h », « 2 j ». */
function elapsedText(ms: number): string {
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  return hours < 24 ? `${hours} h` : `${Math.floor(hours / 24)} j`;
}

/**
 * Ce que la carte dit de l'agent, d'apres l'heure de son dernier signe de vie et l'heure `now` (en
 * millisecondes) : « vu il y a moins d'une minute », « vu il y a 2 min » jusqu'a dix minutes, « éteint
 * depuis 3 h » au-dela, « jamais vu » tant qu'il ne s'est jamais manifeste. Un signe de vie dans le futur
 * (l'horloge du serveur avance) vaut un signe tout frais ; une heure illisible ne fait pas lever.
 */
export function agentStatus(agent: { readonly vuLe: string } | null, now: number): AgentStatus {
  if (agent === null) {
    return { tone: "neutre", text: "Agent jamais vu." };
  }
  const seenAt = new Date(agent.vuLe).getTime();
  if (Number.isNaN(seenAt)) {
    return { tone: "problem", text: "Agent : l'heure de son dernier signe de vie est illisible." };
  }
  const age = Math.max(0, now - seenAt);
  if (age < 60_000) {
    return { tone: "ok", text: "Agent vu il y a moins d'une minute." };
  }
  if (age <= AGENT_ACTIF_MAX_MS) {
    return { tone: "ok", text: `Agent vu il y a ${elapsedText(age)}.` };
  }
  return { tone: "problem", text: `Agent éteint depuis ${elapsedText(age)}.` };
}

export function agentVersionText(version: string): string {
  return `Version de l'agent : ${version}.`;
}

/**
 * La pause de la file : elle ne rend aucune tache a l'agent tant qu'un direct est ouvert, et un direct qui reste ouvert a
 * tort apres une longue panne l'arreterait sans bruit. La phrase dit depuis quand elle attend, a l'heure `now` (en
 * millisecondes), comme la carte le dit du signe de vie : « File en pause : un direct est ouvert depuis 3 h. »
 * Un debut dans le futur (l'horloge du serveur avance) vaut un direct tout frais ; un debut illisible ne fait pas
 * lever et ne se chiffre pas, la phrase dit seulement qu'un direct est ouvert.
 */
export function pauseFileText(pause: { readonly depuis: string }, now: number): string {
  const since = new Date(pause.depuis).getTime();
  if (Number.isNaN(since)) {
    return "File en pause : un direct est ouvert.";
  }
  const age = Math.max(0, now - since);
  const duration = age < 60_000 ? "moins d'une minute" : elapsedText(age);
  return `File en pause : un direct est ouvert depuis ${duration}.`;
}

/** L'etat que l'agent a annonce, et son detail s'il en a donne un : du texte de l'agent, ecrit tel quel. */
export function agentDetailText(state: string, detail: string | null): string {
  return detail === null ? `État : ${state}.` : `État : ${state}. ${detail}`;
}

/**
 * Pourquoi une tache est en echec : la cause telle que l'agent l'a ecrite (un code court), le nombre
 * d'essais, et quand elle a fini d'echouer. Une date illisible ou absente disparait de la phrase.
 */
export function failureText(task: ResumeTache): string {
  const cause = task.cause === null ? "Aucune cause notée." : `Cause : ${task.cause}.`;
  const attempts = `${task.essais} essai${task.essais > 1 ? "s" : ""}.`;
  const endedAt = task.finieLe === null ? null : formatTime(task.finieLe);
  return [cause, attempts, endedAt === null ? null : `Échec le ${endedAt}.`]
    .filter((part) => part !== null)
    .join(" ");
}

// --- La carte « Essai d'OBS » -----------------------------------------------------------------------
// L'overlay d'essai (`/overlay/essai`, une source navigateur d'OBS) relaie la scene active a Castellan et
// joue un son. La carte dit la derniere scene recue, et genere la cle de l'overlay : l'adresse a coller
// dans OBS n'est montree qu'a la generation. Le guide `docs/gestes/castellan-obs-essai.md` cite ces
// phrases : en changer une oblige a le relire.

export const ESSAI_TITRE = "Essai d'OBS";
export const ESSAI_CHARGEMENT = "Lecture de la scène d'OBS...";
/** Quand la requete elle-meme echoue (reseau, 4xx, 5xx, reponse hors contrat). */
export const ESSAI_LECTURE_ECHEC = "La scène d'OBS n'a pas pu être lue. Réessaie dans un instant.";
export const ESSAI_PAS_DE_SCENE =
  "Aucune scène reçue : l'overlay d'essai n'a encore rien signalé depuis le démarrage.";
export const ESSAI_CLE_ABSENTE = "Aucune clé d'overlay n'est posée.";
export const ESSAI_GENERER = "Générer la clé";
export const ESSAI_REGENERER = "Régénérer la clé";
export const ESSAI_CONFIRMATION =
  "Régénérer remplace la clé : tous les overlays d'OBS qui l'utilisent cesseront de marcher, et il faudra recoller la nouvelle adresse dans chacune de leurs sources.";
export const ESSAI_CONFIRMER = "Régénérer quand même";
export const ESSAI_ANNULER = "Annuler";
export const ESSAI_GENERATION = "Génération en cours...";
export const ESSAI_CLE_ECHEC = "La clé n'a pas pu être générée. Réessaie dans un instant.";
export const ESSAI_ADRESSE_LABEL = "Adresse de l'overlay d'essai";
export const ESSAI_ADRESSE_NOTE =
  "Montrée cette fois seulement : colle-la dans une source navigateur d'OBS. Régénérer remplace la clé, et l'ancienne adresse ne marche plus.";

/** La derniere scene signalee, l'heure (Paris) et l'overlay qui l'a signalee. */
export function essaiSceneTexte(scene: {
  readonly nom: string;
  readonly depuis: string;
  readonly source: string;
}): string {
  const quand = formatTime(scene.depuis);
  return `Scène active : ${scene.nom}. Reçue ${quand === null ? "à une heure illisible" : `le ${quand}`}, de l'overlay ${scene.source}.`;
}

/** Une cle est posee : depuis quand. Jamais la cle elle-meme, que la base ne garde pas. */
export function essaiCleTexte(creeeLe: string | null): string {
  const quand = creeeLe === null ? null : formatTime(creeeLe);
  return quand === null
    ? "Une clé d'overlay est posée."
    : `Une clé d'overlay est posée depuis le ${quand}.`;
}
